<template>
  <define-confirm>
    <pf-button
      variant="primary"
      :class="{ disabled: wizard.sending, 'pf-v6-u-mb-4xl': !wizard.modal }"
      @click="wizard.confirmOffer()"
      data-ouia-component-id="wizard-action-confirm"
    >
      <template #icon>
        <circle-check-icon />
      </template>
      {{ wizard.modal ? $t("Conferma l'ordine") : $t("Conferma l'offerta") }}
      <input type="radio" v-model="wizard.action" class="input-hidden" name="action" value="confirm" required />
    </pf-button>
  </define-confirm>

  <wizard-form v-if="!hidden" v-bind="{ ...$attrs, ...ouiaProps }" confirm :title="wizard.modal ? $t('Riepilogo ordine') : $t('Riepilogo offerta')">
    <pf-alert v-if="!wizard.offer" variant="warning" :title="$t('Nessun prodotto trovato')" class="pf-v6-u-mt-lg">
      <p>{{ $t("Con le opzioni da te selezionate non ci è al momento possibile generare un'offerta") }}</p>
      <p>
        <i18n-t keypath="Puoi provare a modificare le opzioni scelte oppure contattare il nostro supporto commerciale {0}." tag="span">
          <router-link :to="{ name: 'tickets' }">
            {{ $t('aprendo un ticket') }}
          </router-link>
        </i18n-t>
      </p>
    </pf-alert>

    <template v-else-if="wizard.offer.prodotti.length">
      <offer-detail
        :offerta="wizard.offer"
        hide-totals
        :no-card="wizard.modal"
        :on-remove-products="wizard.modal ? undefined : $event => wizard.removeProducts($event)"
      />

      <pf-flex direction="column" justify-content="flex-end" :direction-lg="wizard.modal ? 'column' : 'row'" align-items="stretch" gap="lg">
        <pf-flex-item>
          <wizard-question :title="$t('Titolo')" v-if="!wizard.modal">
            <template #description>{{ $t("Se vuoi puoi dare un titolo all'offerta:") }}</template>
            <x-input v-model="wizard.title" type="text" name="title" :placeholder="wizard.defaultTitle" />
          </wizard-question>

          <wizard-question v-if="wizard.customOfferNotes.length" :title="$t('Opzioni dedicate a te')">
            <template #description>{{ $t('Seleziona quelle da applicare a questo progetto') }}</template>
            <pf-data-list>
              <pf-data-list-item v-for="con of wizard.customOfferNotes" :key="con.id">
                <pf-data-list-cell no-fill>
                  <pf-checkbox name="custom_notes[]" :value="con.text" :model-value="wizard.customNote.includes(con.text)" @change="changeNote" />
                </pf-data-list-cell>
                <pf-data-list-cell no-fill
                  ><strong>{{ con.title }}</strong></pf-data-list-cell
                >
                <pf-data-list-cell class="pf-m-truncate" :title="con.text">{{ con.text }}</pf-data-list-cell>
              </pf-data-list-item>
            </pf-data-list>
          </wizard-question>

          <wizard-question :title="wizard.customOfferNotes.length ? $t('Altre note') : $t('Note')">
            <template #description>{{ $t('Scrivici ogni tipo di nota o chiarimento relativo al progetto') }}</template>
            <pf-textarea
              v-model="wizard.note"
              :aria-label="wizard.customOfferNotes.length ? $t('Altre note') : $t('Note')"
              name="note"
              :placeholder="$t('Note Progetto')"
              rows="10"
            />
            <p class="pf-v6-u-mt-sm">
              <strong>{{
                $t(
                  'Si prega di non inserire dati sensibili come password, token di accesso, chiavi, dati anagrafici o auth code: verranno ignorati e cancellati.',
                )
              }}</strong>
            </p>
          </wizard-question>
        </pf-flex-item>

        <pf-flex-item v-if="wizard.offer.prodotti.length">
          <wizard-question :title="$t('Riepilogo dei costi')">
            <div v-if="wizard.freePeriod">
              <strong class="free-label">{{ $t('I primi {0} giorni sono gratuiti', [30]) }}</strong>
            </div>

            <pf-description-list v-if="wizard.offer" horizontal class="payment-totals" term-width="30ch">
              <offer-total
                :totali="wizard.offer.totali"
                :vat="wizard.customer === undefined || wizard.customer.vat > 0"
                durata="Annuale"
                :label="$t('Totale ricorrente annuale:')"
                big
                :currency="wizard.offer.currency"
                data-ouia-component-id="wizard-annual-total"
              />
              <offer-total
                :totali="wizard.offer.totali"
                :vat="wizard.customer === undefined || wizard.customer.vat > 0"
                durata="Mensile"
                :label="$t('Totale ricorrente mensile:')"
                big
                :currency="wizard.offer.currency"
                data-ouia-component-id="wizard-monthly-total"
              />
              <offer-total
                :totali="wizard.offer.totali"
                :vat="wizard.customer === undefined || wizard.customer.vat > 0"
                durata="Una tantum"
                :label="$t('Totale una tantum:')"
                big
                :currency="wizard.offer.currency"
                data-ouia-component-id="wizard-unatantum-total"
              />
            </pf-description-list>

            <payment-cashback
              v-if="wizard.offer && wizard.fatturaMe"
              :model-value="wizard.cashback"
              @update:model-value="
                cb => {
                  wizard.cashback = cb;
                }
              "
              :totale-offerta-ivato="wizard.offer.gran_totale_ivato"
              :currency="wizard.offer.currency"
            />
          </wizard-question>

          <template v-if="wizard.modal">
            <teleport v-if="wizard.offer && wizard.canConfirmOffer" :to="toValue(wizard.modalFooter)?.$el">
              <reuse-confirm />
            </teleport>
          </template>

          <pf-flex v-else id="wizard-actions" justify-content="flex-end" direction="column" class="pf-v6-u-mt-2xl">
            <reuse-confirm v-if="wizard.offer && wizard.canConfirmOffer" />

            <pf-button
              v-if="!wizard.fatturaMe"
              variant="secondary"
              :class="{ disabled: wizard.sending }"
              @click="wizard.submit('mailcustomer')"
              data-ouia-component-id="wizard-action-mailcustomer"
            >
              <template #icon>
                <envelope-icon />
              </template>
              {{ $t("Salva l'offerta e inviala via mail al cliente per conferma") }}
              <input type="radio" v-model="wizard.action" class="input-hidden" name="action" value="mailcustomer" required />
            </pf-button>

            <pf-button variant="secondary" :class="{ disabled: wizard.sending }" @click="wizard.submit('mailme')" data-ouia-component-id="wizard-action-mailme">
              <template #icon>
                <envelope-icon />
              </template>
              {{ $t("Salva l'offerta e inviala via mail solamente a me") }}
              <input type="radio" v-model="wizard.action" class="input-hidden" name="action" value="mailme" required />
            </pf-button>

            <pf-button variant="secondary" :class="{ disabled: wizard.sending }" @click="wizard.submit('pdf')" data-ouia-component-id="wizard-action-pdf">
              <template #icon>
                <file-pdf-icon />
              </template>
              {{ $t("Salva l'offerta e scarica PDF senza inviarla via mail") }}
              <input type="radio" v-model="wizard.action" class="input-hidden" name="action" value="pdf" required />
            </pf-button>
          </pf-flex>
        </pf-flex-item>
      </pf-flex>
    </template>
    <pf-empty-state v-else>
      <pf-empty-state-header :title="$t('Nessun prodotto in offerta')" heading-level="h4" />
    </pf-empty-state>
  </wizard-form>
</template>

<style scoped>
:deep(.pf-v6-c-description-list__text) {
  min-width: 0;
  justify-content: flex-end;
  display: flex;
}
</style>

<script lang="ts" setup>
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import { useWizardStep } from '@/wizard';

import OfferDetail from '@/components/offer-detail.vue';
import offerTotal from '@/components/offer-total.vue';
import WizardForm from '../wizard-form.vue';
import WizardQuestion from '../questions/wizard-question.vue';

import PaymentCashback from '../../payment-cashback.vue';
import CircleCheckIcon from '@vue-patternfly/icons/circle-check-icon';
import EnvelopeIcon from '@vue-patternfly/icons/envelope-icon';
import FilePdfIcon from '@vue-patternfly/icons/file-pdf-icon';
import { createReusableTemplate } from '@vueuse/core';
import { toValue } from 'vue';

defineOptions({
  name: 'WizardStepSummary',
  inheritAttrs: false,
});

const props = defineProps<OUIAProps>();
const ouiaProps = useOUIAProps(props);

const { wizard, hidden, isEnabled } = useWizardStep();
const [DefineConfirm, ReuseConfirm] = createReusableTemplate();

defineExpose({
  isEnabled,
});

function changeNote(e: Event) {
  if (e.target instanceof HTMLInputElement) {
    if (e.target.checked) {
      wizard.customNote.push(e.target.value);
    } else {
      wizard.customNote.splice(wizard.customNote.indexOf(e.target.value), 1);
    }
  }
}
</script>
